/***
 * Copyright (c) Myia 2023-2025 - All Rights Reserved
 */

import { Editor } from '@monaco-editor/react';
import { useEffect, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router';

import { useUserContext } from '../Root';

import { Story } from '@/commons/protocols';
import DisplayError, { formatError } from '@/components/atoms/Error';
import SuiteActions from '@/components/suites/SuiteActions';
import { Button } from '@/components/ui/button';
import { toast } from '@/components/ui/use-toast';
import { useGet, useMutate } from '@/net/commons';

const ReadOnlyEditor = (props: { code: string }) => {
    return <Editor height="75vh" defaultLanguage="json" options={{ readOnly: true }} defaultValue={props.code} />;
};

const StoryPage = () => {
    const { projectSlug, suiteSlug, storySlug } = useParams();
    const { hasSuperUserPermissions } = useUserContext().user;
    const { data, error, isLoading, mutate } = useGet<Story>(
        `/bff/project/${projectSlug}/suite/${suiteSlug}/story/slug/${storySlug}`,
    );
    const [storyString, setStoryString] = useState<string | undefined>(undefined);
    const [submiting, setSubmiting] = useState<boolean>(false);
    const [displayError, setDisplayError] = useState<any>(undefined);

    const navigate = useNavigate();

    const { trigger: trigerArchive } = useMutate(`/bff/project/${projectSlug}/suite/${suiteSlug}/story/:id/archive`, {
        revalidate: true,
        method: 'PATCH',
    });

    const handleArchive = async (id: number) => {
        try {
            setDisplayError(undefined);
            setSubmiting(true);
            await trigerArchive({ idParam: id });
            setSubmiting(true);
            toast({
                title: `Archived story ${storySlug}`,
            });
            mutate();
        } catch (err) {
            setSubmiting(false);
            setDisplayError(err);
            toast({
                title: `Error archiving story`,
                description: formatError(err),
                variant: 'destructive',
            });
        }
    };

    const { trigger } = useMutate(`/bff/project/${projectSlug}/suite/${suiteSlug}/trigger`, {
        revalidate: true,
        method: 'POST',
    });

    const handleRun = async () => {
        try {
            // Run button on story card
            if (data?.id) {
                setDisplayError(undefined);
                setSubmiting(true);
                await trigger({ storyIds: [data?.id] });
                toast({
                    title: 'Run created',
                    description: 'Running 1 story.',
                });
                setSubmiting(false);
                // setIsSubmitting(false);
                navigate(`/suite/${projectSlug}/${suiteSlug}/executions`);
            }
        } catch (err) {
            setSubmiting(false);
            setDisplayError(err);
            toast({
                title: `Error starting run`,
                description: formatError(err),
                variant: 'destructive',
            });
        }
    };

    useEffect(() => {
        if (data) {
            try {
                setStoryString(JSON.stringify(data, null, 2));
            } catch (err) {
                setDisplayError(formatError(err));
            }
        }
    }, [data, isLoading]);

    return (
        <div className="sm:w-10/12 md:w-8/12 mx-auto flex flex-col space-y-3">
            <div className="flex flex-row">
                {displayError && <DisplayError error={displayError} />}
                {hasSuperUserPermissions && (
                    <Button asChild className="text-xs whitespace-nowrap">
                        <Link to={`/admin/story/${projectSlug}/${suiteSlug}/${storySlug}`}>Edit (admin)</Link>
                    </Button>
                )}
            </div>
            {data?.archived && (
                <div className="w-full bg-red-100 text-red-500 p-2 rounded-md text-xs">
                    This story has been archived.
                </div>
            )}
            {data?.parentStoryId && (
                <div className="w-full bg-gray-50 text-gray-900 p-2 rounded-md text-xs">
                    This story has been edited from{' '}
                    <Link
                        to={`/story/${projectSlug}/${suiteSlug}/${data.slug}/${data.parentStoryId}`}
                        className="underline"
                        // Force re-render on navigation
                        onClick={() => {
                            setStoryString('');
                        }}
                    >
                        a previous version
                    </Link>
                </div>
            )}
            {projectSlug && suiteSlug && data?.id && data?.archived !== undefined && (
                <div className="flex flex-row w-full items-end justify-end gap-2">
                    {!data.archived && !data.block && (
                        <Button className="h-8" disabled={submiting} onClick={handleRun}>
                            Run {submiting && '...'}
                        </Button>
                    )}
                    <SuiteActions
                        projectSlug={projectSlug}
                        suiteSlug={suiteSlug}
                        storyId={data.id}
                        storySlug={data.slug}
                        storyName={data.name}
                        handleArchive={() => handleArchive(data.id)}
                        submiting={false}
                        enabled={data.enabled}
                        draft={data.draft}
                        maintenance={data.maintenance}
                        archived={data.archived}
                        block={data.block}
                    />
                </div>
            )}
            {error && <DisplayError error={error} />}
            {storyString && <ReadOnlyEditor code={storyString} />}
        </div>
    );
};

export default StoryPage;
